import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
   useColorModeValue,
   Card,
   Grid,
   GridItem,
   Button,
   CardBody,
   CardHeader,
   Heading,
   useToast,
   CardFooter,
   Flex,
   useDisclosure,
   Image,
   Box,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   TableContainer,
   Input,
   Checkbox,
   IconButton,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';

// Redux actions
import { getDaybook } from '../../../../../redux/actions/daybook';
import { getBillTerm } from '../../../../../redux/actions/billTerm';
// import { getCompanies } from '../../../../../redux/actions/company';
import { getItem } from '../../../../../redux/actions/item';
import { getAccount, addAccount, getAccountById } from '../../../../../redux/actions/account';
import {
   deletePurchaseReturnLineItem,
   addPurchaseReturnInvoice,
   updatePurchaseReturnInvoice,
   getPurchaseReturnInvoice,
   getPurchaseReturnInvoices,
} from '../../../../../redux/actions/purchaseReturnInvoice';
import { getMainAndSubHsn } from '../../../../../redux/actions/mainAndSubHsn';
import { getStock } from '../../../../../redux/actions/salesLineDetail';
import { getWarehouse } from '../../../../../redux/actions/warehouse';

// Components
import LineItemsTable from '../../../sales/Pages/SalesInvoiceMaster/LineItemsTable';
import BillTerms from '../../../sales/Pages/SalesInvoiceMaster/BillTerms';
import DeleteDialog from '../../../masters/components/DeleteDialog';
import DaybookForm from '../../../masters/Pages/DaybookMaster/DaybookForm';
import AddEditForm from '../../../masters/Pages/CustomerMaster/AddEditForm';
import TaxableValue from '../../../sales/Pages/SalesInvoiceMaster/TaxableValue';
import GstDropDown from 'components/GSTDropDown/GstDropDown';

// Utils & Hooks
import { renderFormControl, renderSelectFormControl } from 'utils/DynamicFormData';
import { SupplyTypes } from 'utils/dropdownEnum';
import { filterStocks, removeDuplicates } from '../../../../../utils/util';
import useCalculateTaxAmount from 'hooks/useSalesTaxAmount';
import { usePurchaseReturnDaybook } from '../../../../../contexts';
import { getDefaultId, getAccountAddress, getGstTaxType, getGstSubTaxType } from 'utils/util';
import { useUser, useGlobal } from '../../../../../contexts';

// Assets
import phone from 'assets/img/nfts/phone.svg';
import Email from 'assets/img/nfts/email.svg';
import map from 'assets/img/nfts/map.svg';

// Constants
const initialLineItem = {
   typeOfSupply: 'Goods',
   headerId: 1,
   gst: 0,
   netGst: 0,
   gstAmount: 0,
   itemId: 0,
   lineNumber: 1,
   itemRate: 0,
   amount: 0,
   netAmount: 0,
   taxableAmount: 0,
   ratePer: 0,
   uomNoForRate: 0,
   salesprice1: 0,
   salesprice2: 0,
   salesprice3: 0,
   qty1: 0,
   qty2: 0,
   qty3: 0,
   addLess1Percent: 0,
   addLess1Amount: 0,
   addLess2Percent: 0,
   addLess2Amount: 0,
   addLess3Percent: 0,
   addLess3Amount: 0,
   stock: [],
   cgstAmount: '0.00',
   sgstAmount: '0.00',
   igstAmount: '0.00',
   utgstAmount: '0.00',
};

const validationSchema = Yup.object().shape({
   header: Yup.object().shape({
      daybookId: Yup.number().required('Daybook is required').positive('Invalid daybook selected'),
      docno: Yup.string().required('Document number is required').trim(),
      partyNo: Yup.string().required('Party number is required').trim(),
      partyDate: Yup.date().required('Party date is required').max(new Date(), 'Party date cannot be in the future'),
      transactionAt: Yup.date()
         .required('Transaction date is required')
         .max(new Date(), 'Transaction date cannot be in the future'),
      accountId: Yup.number().required('Account is required').positive('Invalid account selected'),
      basicAmount: Yup.number()
         .required('Basic amount is required')
         .min(0, 'Basic amount cannot be negative')
         .test(
            'is-decimal',
            'Amount cannot have more than 2 decimal places',
            (value) => !value || Number.isInteger(value * 100),
         ),
      netAmount: Yup.number().required('Net amount is required'),
   }),

   lineItem: Yup.array()
      .of(
         Yup.object().shape({
            itemRate: Yup.number()
               .required('Item rate is required')
               .min(0, 'Item rate cannot be negative')
               .test(
                  'is-decimal',
                  'Rate cannot have more than 2 decimal places',
                  (value) => !value || Number.isInteger(value * 100),
               ),
            qty1: Yup.number().required('Quantity is required').min(0, 'Quantity cannot be negative'),
            qty2: Yup.number().default(0).min(0, 'Quantity cannot be negative'),
            qty3: Yup.number().default(0).min(0, 'Quantity cannot be negative'),
            amount: Yup.number().required('Amount is required').min(0, 'Amount cannot be negative'),
            ratePer: Yup.number().min(0, 'Rate per cannot be negative'),
            uomNoForRate: Yup.number()
               .integer('UOM number must be a whole number')
               .min(0, 'UOM number cannot be negative'),
            salesprice2: Yup.number().default(0).min(0, 'Sales price cannot be negative'),
            salesprice3: Yup.number().default(0).min(0, 'Sales price cannot be negative'),
            lineNumber: Yup.number()
               .required('Line number is required')
               .positive('Line number must be positive')
               .integer('Line number must be a whole number'),
         }),
      )
      .min(1, 'At least one line item is required'),
});

const PurchaseReturnInvoiceMasterForm = () => {
   // Hooks and context
   const toast = useToast();
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const { id } = useParams();
   const { companyList } = useGlobal();
   const { lastPurchaseReturnDaybook, setDaybookId } = usePurchaseReturnDaybook();
   const textColor = useColorModeValue('gray.800', 'white');

   // State management
   const [row, setRow] = useState({
      header: {
         daybookId: lastPurchaseReturnDaybook?.daybookId || '',
         docno: '',
         transactionAt: new Date().toISOString().split('T')[0],
         companyId: '',
         accountId: '',
         basicAmount: 0,
         additionAmount: '',
         deductionAmount: '',
         netAmount: 0,
         dueDays: '',
         dueAt: '',
         billtoStateId: 618,
         shiptoStateId: 2,
         partyNo: '',
         partyDate: new Date().toISOString().split('T')[0],
         originalBillNo: '',
         originalBillDate: '',
      },
      lineItem: [initialLineItem],
   });

   const [errors, setErrors] = useState({});
   const [billTerms, setBillTerms] = useState([]);
   const [dueDays, setDueDays] = useState(0);
   const [dueAt, setDueAt] = useState('');
   const [showGSTColumn, setShowGSTColumn] = useState(false);
   const [isSameState, setIsSameState] = useState(null);
   const [selectedAccount, setSelectedAccount] = useState(null);
   const [selectedStocks, setSelectedStocks] = useState([]);
   const [selectedSerialNo, setSelectedSerialNo] = useState([]);
   const [searchQuery, setSearchQuery] = useState('');
   const [currentIndex, setCurrentIndex] = useState(null);
   const [stockList, setStockList] = useState([]);
   const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [deleteLineItemId, setDeleteLineItemId] = useState(null);
   const [hasFetchedAccount, setHasFetchedAccount] = useState(false);

   // Modal controls
   const { isOpen, onOpen, onClose } = useDisclosure();
   const { isOpen: isAccountOpen, onOpen: onAccountOpen, onClose: onAccountClose } = useDisclosure();
   const { isOpen: isOpenModal, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure();

   // Refs for form navigation
   const refs = {
      transactionAt: useRef(null),
      dueDays: useRef(null),
      dueAt: useRef(null),
      companyId: useRef(null),
      accountId: useRef(null),
      additionAmount: useRef(null),
      deductionAmount: useRef(null),
   };

   const focusOrder = [
      'transactionAt',
      'dueDays',
      'dueAt',
      'companyId',
      'accountId',
      'additionAmount',
      'deductionAmount',
   ];

   // Selectors
   const compId = localStorage.getItem('companyId');
   const daybookId = localStorage.getItem('daybook');
   const accountId = localStorage.getItem('accountId');

   const itemData = useSelector((state) => state.item?.list);
   const daybookData = useSelector((state) => state.daybook?.list || []);
   const account = useSelector((state) => state?.account?.list);
   const selectAccount = useSelector((state) => state?.account?.single);
   const billTermData = useSelector((state) => state.billTerm?.list);
   const stocks = useSelector((state) => state?.salesLineDetail?.stocks || []);
   const warehouse = useSelector((state) => state?.warehouse?.list);
   const salesInvoice = useSelector((state) => state?.purchaseReturnInvoice?.single);
   // const companyData = useSelector((state) => state?.company?.list || []);
   const glgroup = useSelector((state) => state.glGroup.list.data || []);
   const targetGlGroup = useMemo(() => glgroup.find((group) => group.code === 'K'), [glgroup]);

   const { user } = useUser();

   // Tax calculation hook
   const {
      totals,
      handleQtyChange,
      handleRateChange,
      handleDiscountChange,
      setTotalBillTermAmount,
      setBillTerm,
      finalNetAmount,
   } = useCalculateTaxAmount({ row, setRow, setBillTerms });

   // Effects
   useEffect(() => {
      if (id) {
         dispatch(getPurchaseReturnInvoice(id));
      }
   }, [dispatch, id]);

   useEffect(() => {
      if (accountId) {
         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               accountId: accountId,
            },
         }));
      }
   }, [accountId]);

   useEffect(() => {
      // Initialize data
      dispatch(getAccount({ compId: user?.loginhistory?.companyId, status: true }));
      if (user && user?.loginhistory?.companyId)
         dispatch(getDaybook({ companyId: user?.loginhistory?.companyId, type: 12 }));
      // dispatch(getCompanies());
      dispatch(getItem({ companyId: user.loginhistory.companyId }));
      dispatch(getMainAndSubHsn());
      dispatch(getWarehouse());
   }, [dispatch, user]);

   useEffect(() => {
      if (row?.header?.daybookId || lastPurchaseReturnDaybook?.daybookId) {
         dispatch(
            getBillTerm({
               daybookId: row?.header?.daybookId || lastPurchaseReturnDaybook?.daybookId || 0,
            }),
         );
         const selectedDaybook = daybookData?.find((daybook) => daybook.id === Number(row.header.daybookId));
         setShowGSTColumn(selectedDaybook?.applyGst || false);
      }
   }, [dispatch, lastPurchaseReturnDaybook, row.header.daybookId, daybookData]);

   const handleEditValues = useCallback(async () => {
      if (id && salesInvoice) {
         if (salesInvoice?.accountId && !hasFetchedAccount) {
            const accountbyid = {
               id: Number(salesInvoice?.accountId), // id must be number
               compId: String(user?.loginhistory?.companyId), // compId must be string
            };
            await dispatch(getAccountById(accountbyid));
            setHasFetchedAccount(true);
         }
         setRow({
            header: {
               daybookId: row.header.daybookId || salesInvoice.daybookId,
               docno: salesInvoice.docno,
               transactionAt: new Date(salesInvoice.transactionAt).toISOString().split('T')[0],
               dueAt: new Date(salesInvoice.dueAt).toISOString().split('T')[0],
               dueDays: salesInvoice.dueDays,
               accountId: salesInvoice.accountId,
               basicAmount: salesInvoice.basicAmount,
               netAmount: salesInvoice.netAmount,
               partyNo: salesInvoice.partyNo,
               partyDate: new Date(salesInvoice.partyDate).toISOString().split('T')[0],
               originalBillNo: salesInvoice.originalBillNo,
               originalBillDate: new Date(salesInvoice.originalBillDate).toISOString().split('T')[0],
            },
            lineItem: salesInvoice?.lineitems?.map((item) => ({
               ...item,
               itemRate: item.itemRate || 0,
               salesprice1: item.itemRate || 0,
               gst: item.gstPercent || 0,
               hsnData:
                  item.hsnData ||
                  companyList?.find((company) => company.id.toString() === user?.loginhistory?.companyId.toString())
                     ?.hsndata,
               sacData:
                  item.sacData ||
                  companyList?.find((company) => company.id.toString() === user?.loginhistory?.companyId.toString())
                     ?.sacdata,
               stock: item.purchaseReturnLineDetail?.map((i) => ({
                  ...i,
                  pcs: i.quantity1,
               })),
            })),
         });

         const initialBillTerms = billTermData?.map((term) => {
            const existingTerm = salesInvoice.purchasebillteams?.find((billTerm) => billTerm.billTermId === term.id);
            return existingTerm
               ? {
                  id: existingTerm.id,
                  billTermId: term.id,
                  percentage: existingTerm.percentage || 0,
                  amount: existingTerm.amount || 0,
               }
               : { billTermId: term.id, percentage: 0, amount: 0 };
         });
         setBillTerms(initialBillTerms);
      }
   }, [billTermData, id, row.header.daybookId, salesInvoice, user?.loginhistory?.companyId]);

   // billTermData, id, row.header.daybookId, salesInvoice, user?.loginhistory?.companyId

   useEffect(() => {
      handleEditValues();
   }, [handleEditValues]);

   useEffect(() => {
      if (lastPurchaseReturnDaybook?.docno) {
         const currentDocNo = String(lastPurchaseReturnDaybook.docno || '0');
         const nextDocNo = isNaN(parseInt(currentDocNo)) ? '1' : String(parseInt(currentDocNo) + 1);

         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               daybookId: lastPurchaseReturnDaybook?.daybookId,
               docno: nextDocNo,
            },
            lineItem: prev.lineItem?.map((item) => ({
               ...item,
               typeOfSupply: SupplyTypes.find((item) => item.id === lastPurchaseReturnDaybook?.typeOfSupply)?.name,
            })),
         }));
      } else {
         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               docno: '1',
            },
            lineItem: prev.lineItem?.map((item) => ({
               ...item,
               typeOfSupply: SupplyTypes.find((item) => item.id === lastPurchaseReturnDaybook?.typeOfSupply)?.name,
            })),
         }));
      }
   }, [dispatch, lastPurchaseReturnDaybook]);

   useEffect(() => {
      if (row.header.transactionAt && dueDays !== undefined) {
         const transactionDate = new Date(row.header.transactionAt);
         const newDueAt = new Date(transactionDate);
         newDueAt.setDate(transactionDate.getDate() + parseInt(dueDays));
         const calculatedDueAt = newDueAt.toISOString().split('T')[0];
         setDueAt(calculatedDueAt);
         handleInputChange('header', 'dueAt', calculatedDueAt);
      }
   }, [dueDays, row.header.transactionAt]);

   useEffect(() => {
      if (daybookId) {
         setDaybookId(daybookId);
      }
   }, [daybookId, setDaybookId]);

   useEffect(() => {
      if (row?.header?.accountId) {
         const acc = account?.find((a) => a?.id?.toString() === row?.header?.accountId?.toString());
         const state = acc?.accountaddress?.find((a) => a.name === 'billing')?.stateId;
         const companyState = companyList?.find((c) => c.id?.toString() === user?.loginhistory?.companyId?.toString());
         setIsSameState(state?.toString() === companyState?.stateid?.toString());
      }
   }, [account, companyList, row?.header?.accountId, user?.loginhistory?.companyId]);

   // Helper functions
   const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      return d instanceof Date && !isNaN(d) ? d.toISOString() : null;
   };

   const clearFormData = () => {
      setRow({
         header: {
            daybookId: '',
            docno: '',
            transactionAt: new Date().toISOString().split('T')[0],
            companyId: '',
            accountId: '',
            basicAmount: 0,
            additionAmount: '',
            deductionAmount: '',
            netAmount: 0,
            dueDays: '',
            dueAt: '',
            billtoStateId: '',
            shiptoStateId: '',
            partyNo: '',
            partyDate: new Date().toISOString().split('T')[0],
            originalBillNo: '',
            originalBillDate: '',
         },
         lineItem: [initialLineItem],
      });
      setErrors({});
      setSelectedStocks([]);
      setSearchQuery('');
      setCurrentIndex(null);
      setSelectedSerialNo([]);
   };

   const handleKeyDown = (e, currentField) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         const currentIndex = focusOrder.indexOf(currentField);
         const nextField = focusOrder[currentIndex + 1];

         if (nextField && refs[nextField]?.current) {
            refs[nextField].current.focus();
            if (refs[nextField].current.tagName === 'SELECT') {
               refs[nextField].current.click();
            }
         }
      }
   };

   const renderFormControlWithRef = (name, label, props, errors, textColor, type, required) => {
      return renderFormControl(
         name,
         label,
         {
            ...props,
            ref: refs[name],
            onKeyDown: (e) => handleKeyDown(e, name),
         },
         errors,
         textColor,
         type,
         required,
      );
   };

   const renderSelectFormControlWithRef = (name, label, options, props, errors, textColor, required, placeholder) => {
      return renderSelectFormControl(
         name,
         label,
         options,
         {
            ...props,
            ref: refs[name],
            onKeyDown: (e) => handleKeyDown(e, name),
         },
         errors,
         textColor,
         required,
         placeholder,
      );
   };

   const handleItemSelection = useCallback(
      (value, index) => {
         const selectedItem = itemData?.find((item) => item.id.toString() === value.toString());
         setRow((prev) => ({
            ...prev,
            lineItem: prev.lineItem?.map((item, i) => {
               const lineItem = selectedItem?.salesprice1 || salesInvoice?.lineitems[index]?.itemRate;
               return i === index
                  ? {
                     ...item,
                     salesprice1: lineItem,
                     itemRate: lineItem,
                     hsnData: selectedItem?.hsnData,
                     sacData: selectedItem?.sacData,
                  }
                  : item;
            }),
         }));
      },
      [itemData, salesInvoice?.lineitems],
   );

   const handleStockModal = useCallback(
      (itemId, index) => {
         const selectedItem = itemData.find((item) => item.id.toString() === itemId.toString());

         if (selectedItem?.itemdetailtype === 1) {
            setCurrentIndex(index);
            onOpenModal();
         }
      },
      [itemData, onOpenModal],
   );

   const handleTypeOfSupplyChange = useCallback((value, index) => {
      setRow((prev) => {
         return {
            ...prev,
            lineItem: prev.lineItem?.map((a, idx) => {
               let subHsn;
               if (value.toUpperCase() === 'GOODS') {
                  subHsn = a?.hsnData?.subHsn.find((hsn) => a.itemRate >= hsn.fromRate && a.itemRate <= hsn.toRate);
               }
               if (value.toUpperCase() === 'SERVICE') {
                  subHsn = a?.sacData?.subHsn.find((hsn) => a.itemRate >= hsn.fromRate && a.itemRate <= hsn.toRate);
               }
               const taxRate = parseFloat(subHsn?.gst) / 100;
               return idx === index
                  ? {
                     ...a,
                     gst: subHsn?.gst ?? 0,
                     gstAmount: a.salesprice1 * a.qty1 * taxRate,
                     ratio: subHsn?.ratio ?? null,
                  }
                  : a;
            }),
         };
      });
   }, []);

   // Event handlers
   const handleInputChange = useCallback(
      (section, field, value, index = null) => {
         if (section === 'header' && field === 'daybookId') {
            dispatch(getBillTerm({ daybookId: value }));
            setDaybookId(value);
            setRow((prev) => ({
               ...prev,
               header: {
                  ...prev.header,
                  daybookId: Number(value),
               },
            }));
         }

         if (section === 'header' && field === 'accountId') {
            const accountbyid = {
               id: Number(value), // id must be number
               compId: String(user?.loginhistory?.companyId), // compId must be string
            };
            dispatch(getAccountById(accountbyid));
         }

         if (section === 'lineItem' && index !== null) {
            setRow((prev) => {
               const updatedLineItems = [...prev.lineItem];
               updatedLineItems[index][field] = Number(value || 0);

               if (['qty1', 'qty2', 'qty3'].includes(field)) {
                  handleQtyChange(field, value, index);
               }
               if (['itemRate', 'salesprice1'].includes(field)) {
                  handleRateChange(field, value, index);
               }
               if (['addLess1Percent', 'addLess2Percent', 'addLess3Percent'].includes(field)) {
                  handleDiscountChange(field, value, index);
               }

               // Recalculate totals
               const totals = updatedLineItems.reduce(
                  (acc, item) => ({
                     basicAmount: acc.basicAmount + (parseFloat(item.itemRate) || 0) * (parseFloat(item.qty1) || 0),
                     gstAmount: acc.gstAmount + (parseFloat(item.gstAmount) || 0),
                     cgstAmount: acc.cgstAmount + (parseFloat(item.cgstAmount) || 0),
                     sgstAmount: acc.sgstAmount + (parseFloat(item.sgstAmount) || 0),
                     igstAmount: acc.igstAmount + (parseFloat(item.igstAmount) || 0),
                     utgstAmount: acc.utgstAmount + (parseFloat(item.utgstAmount) || 0),
                  }),
                  {
                     basicAmount: 0,
                     gstAmount: 0,
                     cgstAmount: 0,
                     sgstAmount: 0,
                     igstAmount: 0,
                     utgstAmount: 0,
                  },
               );

               const newTotalTaxableValue = updatedLineItems.reduce(
                  (sum, item) => sum + (parseFloat(item.salesprice1) || 0) * (parseFloat(item.qty1) || 0),
                  0,
               );

               // setTotalTaxableValue(newTotalTaxableValue.toFixed(2));

               return {
                  ...prev,
                  lineItem: updatedLineItems,
                  header: {
                     ...prev.header,
                     basicAmount: totals.basicAmount.toFixed(2),
                     netAmount: (totals.basicAmount + totals.gstAmount).toFixed(2),
                  },
               };
            });

            if (field === 'typeOfSupply') {
               handleTypeOfSupplyChange(value, index);
            }

            if (field === 'itemId') {
               handleItemSelection(value, index);
               dispatch(getStock({ itemId: Number(value), companyId: user?.loginhistory?.companyId, type: "purchase_return", }));
               handleStockModal(value, index);
            }
         } else if (section === 'header') {
            if (field === 'transactionAt') {
               setRow((prev) => ({
                  ...prev,
                  header: { ...prev.header, partyDate: value },
               }));
            }

            setRow((prev) => {
               const updatedRow = {
                  ...prev,
                  header: {
                     ...prev.header,
                     [field]: value,
                  },
               };

               if (['basicAmount', 'additionAmount', 'deductionAmount'].includes(field)) {
                  const basicAmount = parseFloat(updatedRow.header.basicAmount) || 0;
                  const additionAmount = parseFloat(updatedRow.header.additionAmount) || 0;
                  const deductionAmount = parseFloat(updatedRow.header.deductionAmount) || 0;
                  updatedRow.header.netAmount = basicAmount + additionAmount - deductionAmount;
               }

               return updatedRow;
            });
         }
      },
      [
         user?.loginhistory?.companyId,
         dispatch,
         handleDiscountChange,
         handleItemSelection,
         handleQtyChange,
         handleRateChange,
         handleStockModal,
         handleTypeOfSupplyChange,
         setDaybookId,
      ],
   );

   const handleStockModalOnClick = useCallback(
      async (index) => {
         const itemData = row.lineItem[Number(index)];
         await dispatch(getStock({ itemId: itemData.itemId, companyId: user?.loginhistory?.companyId, type: "purchase_return", }));
         setStockList((prev) => prev.concat(itemData.stock));
         setCurrentIndex(index);
         onOpenModal();
      },
      [dispatch, onOpenModal, row.lineItem, user?.loginhistory?.companyId],
   );

   const handleBillTermChange = (index, values) => {
      setBillTerms((prev) => {
         const newBillTerms = [...prev];
         newBillTerms[index] = {
            ...newBillTerms[index],
            ...values,
         };

         const currentBasicAmount = parseFloat(row.header.basicAmount) || 0;
         let additionTotal = 0;
         let deductionTotal = 0;

         newBillTerms.forEach((term, idx) => {
            const amount = parseFloat(term.amount) || 0;
            const termData = billTermData[idx];

            if (termData?.signname === 'Subtraction') {
               deductionTotal += Math.abs(amount);
            } else if (termData?.signname === 'Addition') {
               additionTotal += Math.abs(amount);
            } else if (termData?.signname === 'Both') {
               if (amount < 0) {
                  deductionTotal += Math.abs(amount);
               } else {
                  additionTotal += amount;
               }
            }
         });

         const netAmount = currentBasicAmount + additionTotal - deductionTotal;

         let billTerm = additionTotal - deductionTotal;
         let netTotal = totals.netAmount + additionTotal;
         netTotal = netTotal - deductionTotal;
         setBillTerm(billTerm);
         setTotalBillTermAmount(netTotal);

         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               additionAmount: additionTotal.toFixed(2),
               deductionAmount: deductionTotal.toFixed(2),
               netAmount: netAmount.toFixed(2),
               basicAmount: currentBasicAmount.toFixed(2),
            },
         }));

         return newBillTerms;
      });
   };

   const addLineItem = () => {
      setRow((prev) => ({
         ...prev,
         lineItem: [...prev.lineItem, { ...initialLineItem }],
      }));
   };

   const removeLineItem = (index, id) => {
      if (id) {
         setDeleteLineItemId(id);
         setDeleteDialogOpen(true);
      }
      if (row.lineItem?.length > 1) {
         setRow((prev) => ({
            ...prev,
            lineItem: prev.lineItem.filter((_, i) => i !== index),
         }));
      }
   };

   const removeStock = useCallback(
      (stock) => {
         setSelectedStocks((prev) => {
            const updatedStocks = prev.filter((e) => e.serialNo !== stock.serialNo);

            setRow((prev) => ({
               ...prev,
               lineItem: prev.lineItem.map((item, idx) => {
                  const stockData = updatedStocks.filter((stock) => stock.itemId === item.itemId);
                  if (stock.itemId === item.itemId) {
                     const qty1 = stockData?.length;
                     if (stockData?.length > 0) handleQtyChange('qty1', qty1, idx);
                     return { ...item, stock: stockData, qty1: qty1 };
                  } else {
                     return item;
                  }
               }),
            }));

            return updatedStocks;
         });
      },
      [handleQtyChange],
   );

   const handleSelectStock = useCallback(
      (e, stock) => {
         if (e.target.checked) {
            setSelectedStocks((prev) => {
               const updatedStocks = [...prev];
               updatedStocks.push({
                  itemId: stock.itemId,
                  itemName: stock.itemName,
                  pcs: stock.pcs,
                  serialNo: stock.serialNo,
               });

               setRow((prev) => ({
                  ...prev,
                  lineItem: prev.lineItem.map((item, idx) => {
                     const stockData = updatedStocks.filter((stock) => stock.itemId === item.itemId);
                     if (stock.itemId === item.itemId) {
                        const qty1 = stockData?.length;
                        if (stockData?.length > 0) handleQtyChange('qty1', qty1, idx);
                        return { ...item, stock: stockData, qty1: qty1 };
                     } else {
                        return item;
                     }
                  }),
               }));

               return updatedStocks;
            });
         }
      },
      [handleQtyChange],
   );

   const handleDelete = () => {
      dispatch(deletePurchaseReturnLineItem(deleteLineItemId));
      setDeleteLineItemId(null);
      setDeleteDialogOpen(false);
   };

   const handleAccountSave = async (data) => {
      await dispatch(addAccount(data));
      dispatch(getAccount({ compId: user?.loginhistory?.companyId, status: true }));
      const storedAccountData = localStorage.getItem('accountData');
      const parsedAccountData = storedAccountData ? JSON.parse(storedAccountData) : null;

      setSelectedAccount({
         label: parsedAccountData.name,
         value: parsedAccountData.id,
         ...parsedAccountData,
      });

      localStorage.removeItem('accountId');
      onAccountClose();
   };

   const validateForm = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const newErrors = {};
            err.inner.forEach((error) => {
               newErrors[error.path] = error.message;
            });
            setErrors(newErrors);
            return false;
         }
         return false;
      }
   };

   const handleSubmit = async () => {
      const isValid = await validateForm();

      if (isValid) {
         const payload = {
            header: {
               ...row.header,
               ...(id && { id: id }),
               basicAmount: Number(totals.netAmount),
               netAmount: Number(finalNetAmount),
               companyId: Number(user?.loginhistory?.companyId),
               accountId: Number(row.header.accountId),
               daybookId: Number(row.header.daybookId),
               additionAmount: Number(row.header.additionAmount) || 0,
               deductionAmount: Number(row.header.deductionAmount) || 0,
               dueDays: dueDays ? Number(dueDays) : 0,
               partyNo: row.header.partyNo,
               partyDate: formatDate(row.header.partyDate),
               originalBillNo: row.header.originalBillNo,
               originalBillDate: formatDate(row.header.originalBillDate),
               transactionAt: formatDate(row.header.transactionAt),
               dueAt: formatDate(row.header.dueAt),
               gstAmount: totals.netGstAmount || 0,
               warehouseId: getDefaultId(warehouse, 'DEFAULT'),
               taxableAmount: totals.taxableAmount || 0,
               billtoStateId: getAccountAddress(account, row.header.accountId)?.billingAddress?.id ?? null,
               shiptoStateId: getAccountAddress(account, row.header.accountId)?.shippingAddress?.id ?? null,
               gstTxnType: getGstTaxType(companyList, account, 'salse'),
               gstSubTxnType: getGstSubTaxType(companyList, account, 'salse'),
               assesseType: account?.assesseType || 0,
               distance: account?.distanceKM || 0,
            },
            lineItem: row.lineItem?.map((items) => ({
               ...(items.id && { id: items.id }),
               typeOfSupply: items.typeOfSupply || 'Goods',
               itemId: items.itemId ? Number(items.itemId) : 0,
               lineNumber: items.lineNumber ? Number(items.lineNumber) : 1,
               itemRate: items.itemRate ? Number(items.itemRate) : 0,
               amount: items.amount ? Number(items.amount) : 0,
               netAmount: items.netAmount ? Number(items.netAmount) : 0,
               ratePer: items.ratePer ? Number(items.ratePer) : 0,
               uomNoForRate: items.uomNoForRate ? Number(items.uomNoForRate) : 0,
               qty1: items.qty1 ? Number(items.qty1) : 0,
               qty2: items.qty2 ? Number(items.qty2) : 0,
               qty3: items.qty3 ? Number(items.qty3) : 0,
               addLess1Percent: items.addLess1Percent ? Number(items.addLess1Percent) : 0,
               addLess1Amount: items.addLess1Amount || 0,
               addLess2Percent: items.addLess2Percent ? Number(items.addLess2Percent) : 0,
               addLess2Amount: items.addLess2Amount || 0,
               addLess3Percent: items.addLess3Percent ? Number(items.addLess3Percent) : 0,
               addLess3Amount: items.addLess3Amount || 0,
               taxableAmount: items.taxableAmount || 0,
               netGst: items.netGst || 0,
               gstPercent: items.gst || 0,
               cgst: totals.taxableAmount,
               sgst: totals.taxableAmount,
               igst: totals.taxableAmount,
               stock: items.stock?.length > 0 ? items.stock : [],
            })),
            purchaseBillTerms: billTerms?.map((term) => ({
               ...(term.id && { id: term.id }),
               billTermId: Number(term.billTermId),
               percentage: Number(term.percentage),
               amount: Number(term.amount),
            })),
         };

         if (id) {
            await dispatch(updatePurchaseReturnInvoice(payload));
         } else {
            await dispatch(addPurchaseReturnInvoice(payload));
         }

         dispatch(getDaybook({ companyId: user?.loginhistory?.companyId, type: 5 }));
         await dispatch(
            getPurchaseReturnInvoices({
               page: 1,
               limit: 10,
               companyId: user?.loginhistory?.companyId,
               sortBy: 'id',
               sortOrder: 'desc',
            }),
         );

         localStorage.removeItem('daybook');
         localStorage.removeItem('accountId');
         clearFormData();
         navigate('/admin/purchase/return');
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please fill in all required fields correctly.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   useEffect(() => {
      const processAllItems = () => {
         let allNewStocks = [];
         let allNewSerialNos = [];
         setSelectedStocks((prevSelectedStocks) => {
            const existingStocks = new Set(prevSelectedStocks.map((stock) => JSON.stringify(stock)));
            const existingSerialNos = new Set(prevSelectedStocks.map((stock) => stock.serialNo));

            row.lineItem.forEach((item) => {
               const itemNewStocks =
                  item?.stock?.filter(
                     (stock) => !existingStocks.has(JSON.stringify(stock)) && !existingSerialNos.has(stock.serialNo),
                  ) || [];

               allNewStocks = [...allNewStocks, ...itemNewStocks];

               const itemNewSerialNos = itemNewStocks.map((stock) => stock.serialNo);
               allNewSerialNos = [...allNewSerialNos, ...itemNewSerialNos];

               itemNewStocks.forEach((stock) => {
                  existingStocks.add(JSON.stringify(stock));
                  existingSerialNos.add(stock.serialNo);
               });
            });

            if (allNewSerialNos?.length > 0) {
               setSelectedSerialNo((prev) => [...prev, ...allNewSerialNos]);
            }

            return [...prevSelectedStocks, ...allNewStocks];
         });
      };

      if (row.lineItem && row.lineItem?.length > 0) {
         processAllItems();
      }
   }, [row.lineItem]);

   useEffect(() => {
      setStockList(stocks);
   }, [stocks]);

   useEffect(() => {
      if (selectAccount) {
         setSelectedAccount(selectAccount);
      }
   }, [selectAccount]);

   return (
      <>
         <Card
            flexDirection="column"
            w="100%"
            px="0px"
            overflowX={{ sm: 'scroll', lg: 'hidden' }}
            mt="-150px"
            position="relative"
            minH={{ base: '50vh', lg: '60vh', xl: '90vh' }}
            maxH={{ base: '50vh', lg: '60vh', xl: '90vh' }}
            overflowY={'scroll'}>
            <CardHeader>
               <Heading size="md">{id ? 'Update' : 'Add'} Purchase Return Invoice</Heading>
            </CardHeader>

            <CardBody>
               <Grid display="flex">
                  <Grid width="55%">
                     <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <GridItem colSpan={1}>
                           <Flex align="center">
                              {renderSelectFormControlWithRef(
                                 'daybookId',
                                 'Daybook',
                                 daybookData.filter((item) => item.type === 12),
                                 {
                                    value: row.header.daybookId,
                                    onChange: (value) => handleInputChange('header', 'daybookId', value),
                                    focusBorderColor: 'blue.500',
                                    _focus: {
                                       boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                    },
                                 },
                                 errors,
                                 textColor,
                                 true,
                                 'Select Daybook',
                              )}
                              <Button variant="ghost" onClick={() => onOpen()}>
                                 <FaPlus style={{ marginLeft: 4 }} />
                              </Button>
                           </Flex>
                        </GridItem>

                        <GridItem>
                           {renderFormControlWithRef(
                              'docno',
                              'Document Number',
                              {
                                 value: row.header.docno,
                                 onChange: (e) => handleInputChange('header', 'docno', e.target.value),
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                              },
                              errors,
                              textColor,
                              'text',
                              true,
                           )}
                        </GridItem>
                     </Grid>

                     <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <GridItem colSpan={1}>
                           {renderFormControlWithRef(
                              'transactionAt',
                              'Transaction Date',
                              {
                                 value: row.header.transactionAt,
                                 onChange: (e) => handleInputChange('header', 'transactionAt', e.target.value),
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                              },
                              errors,
                              textColor,
                              'date',
                              true,
                           )}
                        </GridItem>
                        <GridItem>
                           <Flex align="center" width="100%">
                              <GstDropDown
                                 // ref={nextInputRef}
                                 handleInputChange={handleInputChange}
                                 targetGlGroup={targetGlGroup}
                                 selectedAccount={selectedAccount}
                                 setSelectedAccount={setSelectedAccount}
                              />
                           </Flex>
                        </GridItem>
                     </Grid>
                  </Grid>

                  {selectedAccount && selectedAccount.gstRegNo && selectedAccount.email ? (
                     <Grid
                        border="1.5px solid"
                        borderColor="gray.200"
                        borderRadius="15px"
                        padding="20px"
                        marginLeft="40px"
                        width="45%"
                        display="flex">
                        <Grid>
                           {selectedAccount.mobile ? (
                              <GridItem display="flex" alignItems="center">
                                 <Image src={phone} alt="editicon" boxSize="15px" mr={2} />
                                 <span>{selectedAccount.mobile}</span>
                              </GridItem>
                           ) : null}

                           {selectedAccount.billingaddressname ? (
                              <Grid justify-items="start" marginTop="20px">
                                 <Box fontWeight="500">Billing Address</Box>
                                 <GridItem display="flex" alignItems="flex-start" paddingTop="10px">
                                    <Image src={map} alt="editicon" boxSize="15px" mr={2} mt={1} />
                                    <>
                                       {selectedAccount.billingaddressname.substring(
                                          0,
                                          selectedAccount.billingaddressname.indexOf(',') + 13,
                                       )}{' '}
                                       <br />
                                       {selectedAccount.billingaddressname.substring(
                                          selectedAccount.billingaddressname.indexOf(',') + 14,
                                       )}
                                    </>
                                 </GridItem>
                              </Grid>
                           ) : null}
                        </Grid>
                        <Grid marginLeft="60px">
                           {selectedAccount.email ? (
                              <GridItem display="flex" alignItems="center" height="24px" marginBottom="0px">
                                 <Image src={Email} alt="editicon" boxSize="15px" mr={2} />
                                 <span>{selectedAccount.email}</span>
                              </GridItem>
                           ) : null}

                           {selectedAccount.address1 ? (
                              <Grid justify-items="start" marginTop="20px">
                                 <Box fontWeight="500">Shipping Address</Box>
                                 <GridItem display="flex" paddingTop="10px">
                                    <Image src={map} alt="editicon" boxSize="15px" mr={2} />
                                    <>
                                       {selectedAccount.address1.substring(
                                          0,
                                          selectedAccount.address1.indexOf(',') + 13,
                                       )}{' '}
                                       <br />
                                       {selectedAccount.address1.substring(selectedAccount.address1.indexOf(',') + 14)}
                                    </>
                                 </GridItem>
                              </Grid>
                           ) : null}
                        </Grid>
                     </Grid>
                  ) : null}
               </Grid>

               <Grid templateColumns="repeat(4, 1fr)" gap={6}>
                  <GridItem>
                     <Flex align="center">
                        {renderFormControlWithRef(
                           'partyNo',
                           'Party Number',
                           {
                              value: row.header.partyNo,
                              onChange: (e) => handleInputChange('header', 'partyNo', e.target.value),
                              focusBorderColor: 'blue.500',
                              _focus: {
                                 boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                              },
                           },
                           errors,
                           textColor,
                           'text',
                           true,
                        )}
                     </Flex>
                  </GridItem>

                  <GridItem colSpan={1}>
                     {renderFormControlWithRef(
                        'partyDate',
                        'Party Date',
                        {
                           value: row.header.partyDate,
                           onChange: (e) => handleInputChange('header', 'partyDate', e.target.value),
                           focusBorderColor: 'blue.500',
                           _focus: {
                              boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                           },
                        },
                        errors,
                        textColor,
                        'date',
                        true,
                     )}
                  </GridItem>
               </Grid>

               <Grid templateColumns="repeat(4, 1fr)" gap={6}>
                  <GridItem>
                     <Flex align="center">
                        {renderFormControlWithRef(
                           'originalBillNo',
                           'Original Bill No',
                           {
                              value: row.header.originalBillNo,
                              onChange: (e) => handleInputChange('header', 'originalBillNo', e.target.value),
                              focusBorderColor: 'blue.500',
                              _focus: {
                                 boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                              },
                           },
                           errors,
                           textColor,
                           'text',
                           false,
                        )}
                     </Flex>
                  </GridItem>

                  <GridItem colSpan={1}>
                     {renderFormControlWithRef(
                        'originalBillDate',
                        'Original Bill Date',
                        {
                           value: row.header.originalBillDate,
                           onChange: (e) => handleInputChange('header', 'originalBillDate', e.target.value),
                           focusBorderColor: 'blue.500',
                           _focus: {
                              boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                           },
                        },
                        errors,
                        textColor,
                        'date',
                        false,
                     )}
                  </GridItem>
               </Grid>

               <GridItem colSpan={4}>
                  <LineItemsTable
                     key={row.lineItem?.length}
                     lineItems={row.lineItem}
                     textColor={textColor}
                     itemData={itemData}
                     daybookData={daybookData}
                     showGSTColumn={showGSTColumn}
                     insertItem={addLineItem}
                     removeItem={removeLineItem}
                     handleInputChange={handleInputChange}
                     // updateTotalAmount={updateTotalAmount}
                     // setSelectedItemParent={setSelectedItemParent}
                     refs={refs}
                     // nextInputRef={nextInputRef}
                     errors={errors}
                     lastSaleDaybook={lastPurchaseReturnDaybook}
                     totals={totals}
                     companyData={companyList}
                     handleStockModalOnClick={handleStockModalOnClick}
                  />
               </GridItem>

               <Grid templateColumns="repeat(4, 1fr)" gap="6">
                  <GridItem colSpan={2}>
                     <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={4}>
                        <GridItem>
                           {renderFormControlWithRef(
                              'dueDays',
                              'Due Days',
                              {
                                 value: dueDays,
                                 onChange: (e) => {
                                    const newDueDays = e.target.value;
                                    setDueDays(Number(newDueDays));
                                    handleInputChange('header', 'dueDays', newDueDays);
                                 },
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                              },
                              errors,
                              textColor,
                              'number',
                              true,
                           )}
                        </GridItem>
                        <GridItem>
                           {renderFormControlWithRef(
                              'dueAt',
                              'Due At',
                              {
                                 value: dueAt,
                                 onChange: (e) => {
                                    const newDueAt = e.target.value;
                                    setDueAt(newDueAt);
                                    handleInputChange('header', 'dueAt', newDueAt);
                                 },
                                 focusBorderColor: 'blue.500',
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
                                 },
                              },
                              errors,
                              textColor,
                              'date',
                              true,
                           )}
                        </GridItem>
                     </Grid>
                  </GridItem>
                  <GridItem colSpan={2}>
                     <Flex justifyContent="flex-end">
                        <BillTerms
                           billTermData={row.header.daybookId > 0 ? billTermData : []}
                           billTerms={row.header.daybookId > 0 ? billTerms : []}
                           handleBillTermChange={handleBillTermChange}
                           errors={errors}
                           textColor={textColor}
                           // basicAmount={row.header.basicAmount}
                           basicAmount={totals.netAmount}
                        // netAmount={row.header.netAmount}
                        // netAmount={amounts.netAmount}
                        />
                     </Flex>
                  </GridItem>
               </Grid>

               <Grid templateColumns="repeat(4, 1fr)" gap="6">
                  <GridItem colSpan={2}>
                     <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={4}>
                        {selectedStocks?.length > 0 && (
                           <GridItem>
                              <TableContainer>
                                 <Table variant="simple" border="1px solid #E2E8F0">
                                    <Thead>
                                       <Tr borderBottom="1px solid #E2E8F0">
                                          <Th borderBottom="1px solid #E2E8F0">Item</Th>
                                          <Th borderBottom="1px solid #E2E8F0">Series No</Th>
                                          <Th borderBottom="1px solid #E2E8F0" isNumeric>
                                             Stock
                                          </Th>
                                          <Th borderBottom="1px solid #E2E8F0">Action</Th>
                                       </Tr>
                                    </Thead>
                                    <Tbody>
                                       {selectedStocks &&
                                          removeDuplicates(selectedStocks, 'serialNo')?.map((item, index) => (
                                             <Tr key={index} borderBottom="1px solid #E2E8F0">
                                                <Td borderBottom="1px solid #E2E8F0">{item.itemName}</Td>
                                                <Td borderBottom="1px solid #E2E8F0">{item.serialNo}</Td>
                                                <Td borderBottom="1px solid #E2E8F0" isNumeric>
                                                   {item.pcs}
                                                </Td>
                                                <Td borderBottom="1px solid #E2E8F0">
                                                   <IconButton
                                                      colorScheme="red"
                                                      onClick={() => removeStock(item.serialNo)}
                                                      icon={<FaTrash />}
                                                   />
                                                </Td>
                                             </Tr>
                                          ))}
                                    </Tbody>
                                 </Table>
                              </TableContainer>
                           </GridItem>
                        )}
                     </Grid>
                  </GridItem>

                  <GridItem colSpan={2}>
                     <Flex justifyContent="flex-end">
                        <TaxableValue
                           billTermData={billTermData}
                           billTerms={billTerms}
                           handleBillTermChange={handleBillTermChange}
                           errors={errors}
                           textColor={textColor}
                           basicAmount={row.header.basicAmount}
                           // totalTaxableValue={totalTaxableValue}
                           // totalGSTValues={totalGSTValues}
                           // netAmount={amounts.netAmount}
                           isSameState={isSameState}
                           totals={totals}
                           finalNetAmount={finalNetAmount}
                        />
                     </Flex>
                  </GridItem>
               </Grid>
            </CardBody>

            <CardFooter justifyContent="flex-end">
               <Button
                  onClick={handleSubmit}
                  variant="darkBrand"
                  color="white"
                  fontSize="sm"
                  fontWeight="500"
                  borderRadius="full"
                  px="24px"
                  py="5px"
                  _active={{ bg: '#06b' }}
                  mr={5}>
                  {id ? 'Update' : 'Save'}
               </Button>
               <Button
                  variant="ghost"
                  onClick={() => {
                     clearFormData();
                     navigate('/admin/purchase/return');
                     localStorage.removeItem('daybook');
                     localStorage.removeItem('accountId');
                  }}>
                  Cancel
               </Button>
            </CardFooter>
         </Card>

         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="Delete Line Item"
         />

         <DaybookForm
            isOpen={isOpen}
            onClose={onClose}
         // isEditMode={isEditMode}
         // currentRow={currentRow}
         // onSave={handleSave}
         />

         <AddEditForm
            isOpen={isAccountOpen}
            onClose={onAccountClose}
            // isEditMode={isEditMode}
            // currentRow={currentRow}
            // initialData={currentRow}
            onSave={handleAccountSave}
            parentGlGroup={targetGlGroup?.id}
         />

         <Modal isOpen={isOpenModal} onClose={onCloseModal} size="3xl" isCentered>
            <ModalOverlay />
            <ModalContent
               bg={useColorModeValue('white', '#2d3748')}
               color={useColorModeValue('gray.800', 'white')}
               borderRadius="20px"
               p={5}
               boxShadow="lg">
               <ModalHeader fontSize="20px" fontWeight="600" mb={4}>
                  Add Serial Number
               </ModalHeader>
               <ModalCloseButton />
               <ModalBody>
                  <Grid gap={2}>
                     <Flex justifyContent="space-between" mb={2}>
                        <Box w="100%"></Box>
                        <Input
                           placeholder="Search here..."
                           value={searchQuery}
                           onChange={(e) => setSearchQuery(e.target.value)}
                        />
                     </Flex>
                     <TableContainer>
                        <Table variant="simple" border="1px solid #E2E8F0">
                           <Thead>
                              <Tr borderBottom="1px solid #E2E8F0">
                                 <Th borderBottom="1px solid #E2E8F0"></Th>
                                 <Th borderBottom="1px solid #E2E8F0">Item</Th>
                                 <Th borderBottom="1px solid #E2E8F0">Series No</Th>
                                 <Th borderBottom="1px solid #E2E8F0" isNumeric>
                                    Stock
                                 </Th>
                              </Tr>
                           </Thead>
                           <Tbody>
                              {filterStocks(removeDuplicates(stockList, 'serialNo'), searchQuery).map((item, index) => (
                                 <Tr key={index} borderBottom="1px solid #E2E8F0">
                                    <Td borderBottom="1px solid #E2E8F0">
                                       <Checkbox
                                          isChecked={selectedStocks.map((a) => a.serialNo).includes(item.serialNo)}
                                          onChange={(e) => handleSelectStock(e, item)}
                                       />
                                    </Td>
                                    <Td borderBottom="1px solid #E2E8F0">{item.itemName}</Td>
                                    <Td borderBottom="1px solid #E2E8F0">{item.serialNo}</Td>
                                    <Td borderBottom="1px solid #E2E8F0" isNumeric>
                                       {item.pcs}
                                    </Td>
                                 </Tr>
                              ))}
                           </Tbody>
                        </Table>
                     </TableContainer>
                  </Grid>
               </ModalBody>
               <ModalFooter justifyContent="flex-end" alignItems="center">
                  <Flex>
                     <Button colorScheme="red" onClick={() => onCloseModal()} ml={3} width="100%">
                        Close
                     </Button>
                  </Flex>
               </ModalFooter>
            </ModalContent>
         </Modal>
      </>
   );
};

export default PurchaseReturnInvoiceMasterForm;
